import { Component, ElementRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDatepickerModule,
  MatDateRangePicker,
} from '@angular/material/datepicker';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ja } from 'date-fns/locale';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCard } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { LoadingService } from '@services/loading.service';
import { LogService } from '@services/api/log.service';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import {
  AuditLogListTableComponent,
  AuditLogViewModel,
} from './log-list-table/log-list-table.component';
import { SelectionModel } from '@angular/cdk/collections';
import { subDays, isBefore, parseISO, format } from 'date-fns';
import { ExportService } from '@services/export.service';
import { AuditCsv } from '@entities/log';
import {
  AlertDialogComponent,
  DialogOption as AlertDialogOption,
} from '@components/shared/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';

/** 権限 */
export type Role = 'system-admin' | 'tenant-admin' | 'tenant-user';

/** 基準の期間 */
const MAX_PERIOD = 7;

@Component({
  selector: 'app-log-list',
  standalone: true,
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: ja,
    },
    provideDateFnsAdapter({
      parse: {
        dateInput: 'yyyy/MM/dd',
      },
      display: {
        dateInput: 'yyyy/MM/dd',
        monthYearLabel: 'yyyy/MM',
        dateA11yLabel: 'yyyy/MM/dd',
        monthYearA11yLabel: 'yyyy/MM',
      },
    }),
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonToggleModule,
    AuditLogListTableComponent,
    MatCard,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatDateRangePicker,
    MatLabel,
    ReactiveFormsModule,
  ],
  templateUrl: './log-list.component.html',
  styleUrl: './log-list.component.scss',
})
export class LogListComponent implements OnInit {
  /** 検索フォーム */
  public historyFormGroup: FormGroup;

  /** 権限 */
  public role: Role = 'system-admin';

  /** Auditログ */
  public auditLog?: AuditLogViewModel;

  /** Auditログ一覧 */
  public auditLogList: AuditLogViewModel[] = [];

  /** 操作種別一覧 */
  public operationTypeList: string[] = [];

  /** 2要素認証設定一覧 */
  public mfaSettingList: string[] = [];

  /** デバイス関連タグ一覧 */
  public deviceTagRefsList: string[] = [];

  /** デバイス種類ID一覧 */
  public deviceTypeIdList: string[] = [];

  /** グループ化されたデバイス種類ID一覧 */
  public groupDeviceTypeIdList: string[] = [];

  /** 選択状態 */
  public selection = new SelectionModel<AuditLogViewModel>(true, []);

  /** 所属テナント */
  public currentTenant = '';

  /** 対象期間(開始) */
  public historyFrom: Date;

  /** 対象期間(終了) */
  public historyTo: Date;

  /** CSVのヘッダーを作成するためのマップ */
  private readonly csvColumnMap = [
    { key: 'operationType', label: 'Operation Type' },
    { key: 'userId', label: 'User Id' },
    { key: 'message', label: 'Message' },
    { key: 'date', label: 'Date' },
  ];

  /** エクスポート用のCSVデータモデル */
  public auditCsv: AuditCsv[] = [];

  operationTypeNameMap = new Map([
    ['login', 'ログイン'],
    ['logout', 'ログアウト'],
    ['export_auditlog', 'Auditログエクスポート'],
    ['user_add', 'アカウント追加'],
    ['user_update', 'アカウント編集'],
    ['user_delete', 'アカウント削除'],
    ['export_device_info', 'デバイス詳細エクスポート'],
    ['tracker_update', 'デバイス設定変更'],
    ['device_tag_update', 'デバイスタグ変更'],
    ['device_setting_group_add', 'デバイス設定グループ追加'],
    ['device_setting_group_update', 'デバイス設定グループ更新'],
    ['group_setting_update', 'グループ一括設定'],
    ['device_setting_group_delete', 'デバイス設定グループ削除'],
    ['asset_type_add', '設定テンプレート追加'],
    ['asset_type_update', '設定テンプレート更新'],
    ['asset_type_delete', '設定テンプレート削除'],
    ['mfa_enable', '2要素認証を有効化'],
    ['mfa_disable', '2要素認証を無効化'],
    ['password_change', 'パスワード変更'],
    ['tag_add', 'タグ追加'],
    ['tag_update', 'タグ更新'],
    ['tag_delete', 'タグ削除'],
    ['apikey_add', 'API Key発行'],
    ['apikey_delete', 'API Key破棄'],
    ['tenant_attr_update', 'テナント属性更新'],
    ['webhook_delete', 'Webhook削除'],
    ['notify_email_update', 'アラート通知メールアドレス更新'],
    ['shipment_add', '配送設定追加'],
    ['shipment_update', '配送設定更新'],
    ['shipment_delete', '配送設定削除'],
    ['shipment_route_add', 'ルート設定追加'],
    ['shipment_route_update', 'ルート設定更新'],
    ['shipment_route_delete', 'ルート設定削除'],
    ['shipment_location_add', '位置情報設定追加'],
    ['shipment_location_update', '位置情報設定更新'],
    ['shipment_location_delete', '位置情報設定削除'],
  ]);

  private dateRangeValidator: ValidatorFn = (
    group: AbstractControl,
  ): ValidationErrors | null => {
    const start = group.get('historyFrom')?.value;
    const end = group.get('historyTo')?.value;
    return start && end && start > end ? { dateRange: true } : null;
  };

  constructor(
    private logService: LogService,
    private loadingService: LoadingService,
    private exportService: ExportService,
    private elementRef: ElementRef,
    private dialog: MatDialog,
  ) {
    const now = new Date();
    this.historyFormGroup = new FormGroup(
      {
        historyFrom: new FormControl(subDays(now, 1), {
          validators: [Validators.required],
        }),
        historyTo: new FormControl(now, {
          validators: [Validators.required],
        }),
      },
      { validators: this.dateRangeValidator },
    );
    this.historyFrom = this.historyFormGroup.controls['historyFrom'].value;
    this.historyTo = this.historyFormGroup.controls['historyTo'].value;
  }

  public ngOnInit(): void {
    // デバイス設定一覧を取得する
    this.initAuditLogList();
  }

  /**
   * 指定した期間が基準を超えないか確認する。
   * @returns
   */
  public checkPeriod() {
    const historyFrom = this.historyFormGroup.get('historyFrom')?.value as Date;
    const historyTo = this.historyFormGroup.get('historyTo')?.value as Date;
    // カレンダーで選択したときは日付は0時0分0秒となるためinitAuditLogList時と合わせるよう個別で値をセットする。
    if (historyTo) {
      historyTo.setHours(23);
      historyTo.setMinutes(59);
      historyTo.setSeconds(59);
    }

    const isValidPeriod = !isBefore(
      historyFrom,
      subDays(historyTo, MAX_PERIOD),
    );
    return isValidPeriod;
  }

  /**
   * Auditログの一覧を取得する。
   */
  public initAuditLogList() {
    const historyFrom = this.historyFormGroup.get('historyFrom')?.value as Date;
    const historyTo = this.historyFormGroup.get('historyTo')?.value as Date;

    historyFrom.setHours(0, 0, 0, 0);
    historyTo.setHours(23, 59, 59, 0);

    this.logService
      .findAll(historyFrom.toISOString(), historyTo.toISOString())
      .subscribe({
        next: (auditLog) => {
          this.auditLogList = auditLog.logList.map((auditLog) => ({
            operationType: auditLog.operationType,
            userId: auditLog.userId,
            message: auditLog.description ? auditLog.description : '',
            date: parseISO(auditLog.date),
          }));
          this.auditLogList.forEach((auditLog) => {
            auditLog.operationType =
              this.operationTypeNameMap.get(auditLog.operationType) || '';
          });
          this.auditLogList.sort((first, second) =>
            first.date < second.date ? 1 : -1,
          );

          const operationTypeList = this.auditLogList.map(
            (auditLog) => auditLog.operationType,
          );
          const uniqueOperationTypeList = new Set(operationTypeList);
          this.operationTypeList = Array.from(uniqueOperationTypeList).filter(
            (uniqueOperationType) => uniqueOperationType != '',
          );

          if (auditLog.isOverLimit) {
            const alertDialogOptions: AlertDialogOption = {
              type: 'warning',
              message: 'Auditログ取得件数が1万件を超過しました。',
            };
            this.dialog.open(AlertDialogComponent, {
              width: '600px',
              data: alertDialogOptions,
            });
          }
        },
      });
  }

  /**
   * Auditログの一覧をCSV出力する。
   */
  public async exportToCsv() {
    this.loadingService.show();
    try {
      this.auditCsv = this.auditLogList.map((auditLog) => {
        return {
          operationType: auditLog.operationType,
          userId: auditLog.userId,
          message: auditLog.message,
          date: auditLog.date,
        };
      });

      const now = new Date();
      const formattedDate = format(now, 'yyyyMMddHHmmss');
      const fileName = `auditlogs_${formattedDate}.csv`;

      this.exportService.exportToCsv(
        this.csvColumnMap,
        this.auditCsv,
        fileName,
        true,
        "yyyy-MM-dd'T'HH:mm:ss",
        'Asia/Tokyo',
      );

      // ログの登録自体は失敗しても処理を継続する。
      const params: { [key: string]: string } = {
        history_from: this.historyFrom.toISOString(),
        history_to: this.historyTo.toISOString(),
      };
      this.logService.add('export_auditlog', params).subscribe({
        error: () => {
          // ログ埋め込み処理が失敗した場合でも処理を止めない。
        },
      });
    } catch (error) {
      const alertDialogOptions: AlertDialogOption = {
        type: 'error',
        message: `CSVのエクスポートに失敗しました。\nしばらくしてからもう一度お試しください。`,
      };
      this.dialog.open(AlertDialogComponent, {
        width: '600px',
        data: alertDialogOptions,
      });
    } finally {
      this.loadingService.hide();
    }
  }
}
